/**
 * Utility functions for handling family data and account grouping
 */

import { createSelector } from "reselect"

import {
    extractDeepLinkPath,
    extractPathFromUrl,
    isParentPath,
    isTutorPath,
} from "@common/utils/navigationUtils"

const isTutorToken = (t) =>
    t?.roles?.some((r) => r === "tutor" || r === "tutor-candidate")

/**
 * Groups stored tokens by their families
 * @param {Array} storedTokens - Array of stored authentication tokens
 * @param {Object} familyState - Redux family state with families and userFamilyMap
 * @param {boolean} includeIndividualAsFamily - Whether to include individual accounts as a special family group
 * @returns {Object} Object containing individualAccounts and familyGroups arrays
 */
export function groupAccountsByFamily(
    storedTokens,
    familyState,
    includeIndividualAsFamily = false
) {
    if (!storedTokens || storedTokens.length === 0) {
        return {
            individualAccounts: [],
            familyGroups: [],
        }
    }

    if (!familyState || !familyState.families || !familyState.userFamilyMap) {
        // No family data, return all tokens as individual accounts or as individual family group
        if (includeIndividualAsFamily && storedTokens.length > 0) {
            return {
                individualAccounts: [],
                familyGroups: [
                    {
                        familyId: -1,
                        familyName: "Comptes individuels",
                        familyData: null,
                        accounts: storedTokens,
                        isIndividualGroup: true,
                    },
                ],
            }
        }
        return {
            individualAccounts: storedTokens,
            familyGroups: [],
        }
    }

    const { families, userFamilyMap } = familyState
    const familyGroups = {}
    const individualAccounts = []

    // Group tokens by family using userId
    storedTokens.forEach((token) => {
        const familyId = userFamilyMap[token.userId]

        if (familyId && families[familyId]) {
            // This user belongs to a family
            if (!familyGroups[familyId]) {
                familyGroups[familyId] = {
                    familyId,
                    familyName:
                        families[familyId].name || `Famille ${familyId}`,
                    familyData: families[familyId],
                    accounts: [],
                }
            }
            familyGroups[familyId].accounts.push(token)
        } else {
            // This user doesn't belong to any known family
            individualAccounts.push(token)
        }
    })

    // Convert family groups object to array and filter out single-member families
    const familyGroupsArray = Object.values(familyGroups).filter((group) => {
        if (group.accounts.length === 1) {
            // Single member families should be treated as individual accounts
            individualAccounts.push(...group.accounts)
            return false
        }
        return true
    })

    // If includeIndividualAsFamily is true and we have individual accounts, create a special family group for them
    if (includeIndividualAsFamily && individualAccounts.length > 0) {
        familyGroupsArray.push({
            familyId: -1,
            familyName: "Comptes individuels",
            familyData: null,
            accounts: individualAccounts,
            isIndividualGroup: true,
        })
        return {
            individualAccounts: [],
            familyGroups: familyGroupsArray,
        }
    }

    return {
        individualAccounts,
        familyGroups: familyGroupsArray,
    }
}

/**
 * The stored accounts of the connected session — what every in-session list of
 * accounts reads (siblings, profile switchers, joining a live as a child).
 * Under impersonation the browser still holds the admin's own accounts, kept on
 * purpose for after the exit: they must never be listed next to the
 * impersonated account, nor be one tap away from it. Outside impersonation, and
 * in the auth stack (« Qui utilise Masteur ? »), it is every stored account.
 */
export const selectSessionStoredTokens = createSelector(
    [
        (state) => state?.data?.authTokens?.storedTokens,
        (state) => !!state?.data?.currentUser?.object?.impersonation,
    ],
    (storedTokens, impersonating) => {
        const all = Array.isArray(storedTokens) ? storedTokens : []
        return impersonating
            ? all.filter((token) => token?.is_impersonation)
            : all
    }
)

const isStudentAccount = (account) =>
    account.roles && account.roles.includes("student")

/**
 * The children the parent space lists — the student accounts of the family
 * picked at « Qui utilise Masteur ? » (`parentModeFamilyId`), or, without a
 * pick, of the family of the connected account.
 *
 * @param {Array} storedTokens - The session's stored tokens (`selectSessionStoredTokens`)
 * @param {Object} familyState - Redux family state (with parentModeFamilyId)
 * @param {Object} user - The connected user (currentUser.object)
 * @returns {Array} The stored tokens of the children to list
 */
export function resolveParentFamilyAccounts(storedTokens, familyState, user) {
    const selectedFamilyId = familyState?.parentModeFamilyId
    const { familyGroups } = groupAccountsByFamily(
        storedTokens,
        familyState,
        true // Include individual accounts as a family group
    )

    const group =
        selectedFamilyId !== null
            ? familyGroups.find((candidate) =>
                  selectedFamilyId === -1
                      ? candidate.isIndividualGroup
                      : candidate.familyId === selectedFamilyId
              )
            : groupOfConnectedAccount(familyGroups, familyState, user)
    return (group?.accounts || []).filter(isStudentAccount)
}

// Fallback: anchor on the currently authenticated user instead of blindly
// picking familyGroups[0] (which would surface accounts from a previous login
// that's still in storedTokens). We look up the current user's familyId via
// userFamilyMap; if none, fall back to the first group only as a last resort.
function groupOfConnectedAccount(familyGroups, familyState, user) {
    const currentUserFamilyId =
        user?.id && familyState?.userFamilyMap
            ? familyState.userFamilyMap[user.id]
            : null
    const currentUserGroup = currentUserFamilyId
        ? familyGroups.find((group) => group.familyId === currentUserFamilyId)
        : null
    return (
        currentUserGroup ||
        (user?.id
            ? familyGroups.find((group) =>
                  group.accounts?.some((account) => account.userId === user.id)
              )
            : null) ||
        familyGroups[0]
    )
}

/**
 * Gets family data for a specific user ID
 * @param {number} userId - User's ID
 * @param {Object} familyState - Redux family state
 * @returns {Object|null} Family data or null if not found
 */
export function getFamilyForUser(userId, familyState) {
    if (!familyState || !familyState.families || !familyState.userFamilyMap) {
        return null
    }

    const familyId = familyState.userFamilyMap[userId]
    return familyId ? familyState.families[familyId] : null
}

/**
 * Checks if two users belong to the same family
 * @param {number} userId1 - First user's ID
 * @param {number} userId2 - Second user's ID
 * @param {Object} familyState - Redux family state
 * @returns {boolean} True if they belong to the same family
 */
export function areUsersInSameFamily(userId1, userId2, familyState) {
    if (!familyState || !familyState.userFamilyMap) {
        return false
    }

    const family1 = familyState.userFamilyMap[userId1]
    const family2 = familyState.userFamilyMap[userId2]

    return family1 && family2 && family1 === family2
}

/**
 * Gets all families with their member count
 * @param {Object} familyState - Redux family state
 * @returns {Array} Array of family objects with member counts
 */
export function getAllFamiliesWithCounts(familyState) {
    if (!familyState || !familyState.families || !familyState.userFamilyMap) {
        return []
    }

    const { families, userFamilyMap } = familyState
    const familyCounts = {}

    // Count members for each family
    Object.values(userFamilyMap).forEach((familyId) => {
        familyCounts[familyId] = (familyCounts[familyId] || 0) + 1
    })

    return Object.keys(families).map((familyId) => ({
        ...families[familyId],
        memberCount: familyCounts[familyId] || 0,
    }))
}

/**
 * Gets the first family member token for parent mode
 * @param {Array} storedTokens - Array of stored authentication tokens
 * @param {Object} familyState - Redux family state
 * @returns {Object|null} First family member token or null if not found
 */
export function getFirstFamilyMemberToken(storedTokens, familyState) {
    if (!storedTokens || storedTokens.length === 0) {
        return null
    }

    const { familyGroups } = groupAccountsByFamily(storedTokens, familyState)

    // Get the first family group and return its first account
    if (familyGroups.length > 0 && familyGroups[0].accounts.length > 0) {
        return familyGroups[0].accounts[0]
    }

    return null
}

/**
 * Given a pending deep link URL, stored tokens, and family state, determine
 * whether family-account-selection can be bypassed by auto-logging into a
 * single account. Returns null if no auto-bypass is possible.
 *
 * @returns {{ token: object, isParentMode: boolean, familyId: number|null }|null}
 */
export function resolveAutoBypassTarget(
    pendingDeepLink,
    storedTokens,
    familyState
) {
    if (!pendingDeepLink || !storedTokens?.length) return null

    const path = extractDeepLinkPath(pendingDeepLink)
    if (!path) return null

    const wantsParentMode = isParentPath(path)
    const wantsTutorMode = isTutorPath(path)

    // Tutor paths: auto-login if there's exactly one tutor token. Works for
    // single- or multi-token storage; tutors don't participate in the
    // family-selection flow.
    if (wantsTutorMode) {
        const tutorAccounts = storedTokens.filter(isTutorToken)
        if (tutorAccounts.length !== 1) return null
        return {
            token: tutorAccounts[0],
            isParentMode: false,
            familyId: null,
        }
    }

    // Single account total: auto-login directly — but only if the role matches
    // the target mode. Parent paths require a student token; otherwise fall
    // through to the normal family-selection UI.
    if (storedTokens.length === 1) {
        const only = storedTokens[0]
        if (wantsParentMode && !only.roles?.includes("student")) return null
        const mappedFamilyId = familyState?.userFamilyMap?.[only.userId]
        return {
            token: only,
            isParentMode: wantsParentMode,
            familyId: wantsParentMode ? (mappedFamilyId ?? -1) : null,
        }
    }

    // Multiple accounts: need exactly one family that contains at least one student.
    const { familyGroups } = groupAccountsByFamily(
        storedTokens,
        familyState,
        true
    )
    const groupsWithStudents = familyGroups?.filter((g) =>
        g.accounts?.some((a) => a.roles?.includes("student"))
    )
    if (!groupsWithStudents || groupsWithStudents.length !== 1) return null

    const group = groupsWithStudents[0]
    const familyId = group.isIndividualGroup ? -1 : group.familyId

    if (wantsParentMode) {
        // Un lien qui nomme l'enfant (`student_id`, le mail « Garder son tuteur ») entre sur lui.
        const linkedStudentId = extractPathFromUrl(pendingDeepLink)?.params?.student_id
        const linkedStudent =
            linkedStudentId != null
                ? group.accounts.find(
                      (a) =>
                          a.roles?.includes("student") &&
                          String(a.userId) === String(linkedStudentId)
                  )
                : null
        const firstStudent =
            linkedStudent ??
            group.accounts.find((a) => a.roles?.includes("student"))
        if (!firstStudent) return null
        return {
            token: firstStudent,
            isParentMode: true,
            familyId,
        }
    }

    // Student path: only auto-login if there's a single student in the family.
    const studentAccounts = group.accounts.filter((a) =>
        a.roles?.includes("student")
    )
    if (studentAccounts.length !== 1) return null
    return {
        token: studentAccounts[0],
        isParentMode: false,
        familyId: null,
    }
}
